import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CommonApiService, HttpOptions } from 'polly-commons';
import {
  AddCollaboratorPayload, AtlasExplorePayload, AtlasExploreResponseData, AtlasFilterListResponse, AtlasFilterPayload,
  AtlasPermissionResponse, AtlasQueryListResponse, AtlasQueryPayload, AtlasTableListResponse, AtlasUserResponse,
  IndividualAtlasResponse, PollyAtlasList, UpdateAtlasPermissionPayload, CreateEditAtlasPayload,
  CreateEditAtlasResponse, AtlasPermissionListResponse, AtlasChangeLogResponse
} from '../@core/interfaces';
import { PaginatorData } from 'polly-qubit';
import { generateCreateAtlasPayload, generateEditAtlasPayload } from '../@core/utility';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PollyAtlasBackendService {
  private readonly BASE_ENDPOINT: string = '/sarovar';
  private readonly ATLAS_BASE_ENDPOINT: string = `${this.BASE_ENDPOINT}/atlas`;
  private readonly ATLAS_BASE_ENDPOINT_RBAC: string = `${this.BASE_ENDPOINT}/rbac`;
  private readonly CSV_HEADER_OPTIONS: HttpOptions = {
    headers: {
      'Content-Type': 'text/csv',
    },
    responseType: 'text'
  };
  private readonly envPrefix = environment.environment === 'prod' ? '' : environment.environment === 'test' ? 'test' : 'dev';
  private readonly LOAD_BALANCER_BASE_ENDPOINT = `https://sarovar.${this.envPrefix}polly.elucidata.io`;

  constructor(
    private readonly http: HttpClient,
    private readonly commonsApiService: CommonApiService
  ) { }

  public getAllAtlases(): Observable<PollyAtlasList> {
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}`);
  }

  public createAtlas(atlasName: string, atlasId: string, description: string | null): Observable<CreateEditAtlasResponse> {
    const payload: CreateEditAtlasPayload = generateCreateAtlasPayload(atlasName, atlasId, description);
    return this.commonsApiService.post(`${this.ATLAS_BASE_ENDPOINT}`, payload);
  }

  public archiveAtlas(atlasId: string): Observable<void> {
    return this.commonsApiService.delete(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}`);
  }

  public editAtlas(atlasId: string, atlasName: string, description: string | null): Observable<CreateEditAtlasResponse> {
    const payload: CreateEditAtlasPayload = generateEditAtlasPayload(atlasId, atlasName, description);
    return this.commonsApiService.patch(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}`, payload);
  }

  public getAtlas(atlasId: string): Observable<IndividualAtlasResponse> {
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}`);
  }

  public getAtlasTableInfo(atlas: string): Observable<AtlasTableListResponse> {
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}/${atlas}/tables/`);
  }

  public atlasQuery(atlasId: string, payload: AtlasQueryPayload): Observable<AtlasQueryListResponse> {
    return this.commonsApiService.post(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/queries`, payload, {headers: { QueryType: 'frontend' }});
  }

  public atlasQueryLoadBalanced(atlasId: string, payload: AtlasQueryPayload): Observable<AtlasQueryListResponse> {
    return this.http.post<AtlasQueryListResponse>(`${this.LOAD_BALANCER_BASE_ENDPOINT}/atlas/${atlasId}/queries`, payload, { headers: { 'Content-Type': 'application/vnd.api+json', QueryType: 'frontend'  }});
  }

  public getAtlasERD(atlasId: string): Observable<string> {
    const httpOptions: HttpOptions = { responseType: 'text' };
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/erd.svg`, httpOptions);
  }

  public exploreAtlas(atlasId: string, tableName: string, payload: AtlasExplorePayload, paginationData: PaginatorData): Observable<AtlasExploreResponseData> {
    const paginationUrl = `?page[size]=${paginationData.rows}&page[number]=${paginationData.page}`;
    return this.commonsApiService.post(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/tables/${tableName}/explore/${paginationUrl}&include_count=true`, payload);
  }

  public exploreAtlasFilters(atlasId: string, tableName: string, columnName: string, payload: AtlasFilterPayload, paginatorData: PaginatorData): Observable<AtlasFilterListResponse> {
    const paginationUrl = `?page[size]=${paginatorData.rows}&page[number]=${paginatorData.page}`;
    return this.commonsApiService.post(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/tables/${tableName}/explore/${columnName}/filter_options${paginationUrl}`, payload);
  }

  public downloadQueryResults(atlasId: string, queryId: string): Observable<string> {
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/downloads/${queryId}`, this.CSV_HEADER_OPTIONS);
  }

  public getAtlasPermissions(atlasId: string): Observable<AtlasPermissionListResponse> {
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT_RBAC}/atlas:${atlasId}/permissions`);
  }

  public addAtlasCollaborator(atlasId: string, payload: AddCollaboratorPayload): Observable<AtlasPermissionResponse> {
    return this.commonsApiService.post(`${this.ATLAS_BASE_ENDPOINT_RBAC}/atlas:${atlasId}/permissions`, payload);
  }

  public updateAtlasPermission(atlasId: string, permissionId: string, payload: UpdateAtlasPermissionPayload): Observable<AtlasPermissionResponse> {
    return this.commonsApiService.patch(`${this.ATLAS_BASE_ENDPOINT_RBAC}/atlas:${atlasId}/permissions/${permissionId}`, payload);
  }

  public removeAtlasCollaborator(atlasId: string, permissionId: string): Observable<null> {
    return this.commonsApiService.delete(`${this.ATLAS_BASE_ENDPOINT_RBAC}/atlas:${atlasId}/permissions/${permissionId}`);
  }

  public searchUsers(keyword: string): Observable<AtlasUserResponse> {
    return this.commonsApiService.get(`${this.BASE_ENDPOINT}/principals?s=${keyword}`);
  }

  public getAtlasChangelog(atlasId: string, paginatorData: PaginatorData, selectedFilters?: string[]): Observable<AtlasChangeLogResponse> {
    let filterUrl = '';
    let paginatorUrl = `page[size]=${paginatorData.rows}&page[number]=${paginatorData.page}`;
    if (selectedFilters && selectedFilters.length > 0) {
      filterUrl = selectedFilters.map(filter => `filter[log_type]=${filter}`).join('&');
    }
    return this.commonsApiService.get(`${this.ATLAS_BASE_ENDPOINT}/${atlasId}/logs?${filterUrl}&${paginatorUrl}`);
  }

  public downloadAtlasChangelog(atlasId: string): Observable<string> {
    // TODO | BADAL : Check what to do with Load balancer BASE_ENDPOINT w.r.t Bearer Token
    return this.http.get(`${this.LOAD_BALANCER_BASE_ENDPOINT}/atlas/${atlasId}/logs?download=true`, { responseType: 'text' });
  }

  public downloadFilteredCSV(atlasId: string, tableName: string, payload: AtlasExplorePayload): Observable<Blob> {
    const downloadPayload = { ...payload, data: { ...payload.data, type: 'explore_download' } };
    return this.http.post(`${this.LOAD_BALANCER_BASE_ENDPOINT}/atlas/${atlasId}/tables/${tableName}/explore/download`, downloadPayload,
      {
        headers: { 'Content-Type': 'application/vnd.api+json' },
        responseType: 'blob'
      }
    );
  }
}
